import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import {
  createAnswerItemsFromValues,
  getAnswerValuesFromItems
} from 'components/Assessments/helpers'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { ADLNeedFormDataItem } from './formData'
import { ADLNeedsFormField } from './formField'

export class ADLNeedsAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const adlNeedsAnswers = flatAnswers[SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED]?.answer ?? []
    const adlneedsFormData = adlNeedsAnswers?.map<ADLNeedFormDataItem>((adlAnswerItem) => {
      const adlInfoItem = adlAnswerItem.item ?? []

      const adlId = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_ID
      )?.at(0)

      const adlName = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_NAME
      )?.at(0)

      const adlObservationReference = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_OBSERVATION_REFERENCE
      )?.at(0)

      const adlNewNote = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_NEW_NOTE
      )?.at(0)

      const adlIsArchived = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_IS_ARCHIVED
      )?.at(0)

      const adlLastNoteText = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_TEXT
      )?.at(0)

      const adlLastNoteAuthor = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_AUTHOR
      )?.at(0)

      const adlLastNoteDate = getAnswerValuesFromItems(
        adlInfoItem,
        SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_DATE
      )?.at(0)

      return {
        [ADLNeedsFormField.ADL_NEED_ID]: adlId,
        [ADLNeedsFormField.ADL_NEED_NAME]: adlName,
        [ADLNeedsFormField.ADL_NEED_OBSERVATION_REFERENCE]: adlObservationReference
          ? adlObservationReference.reference
          : undefined,
        [ADLNeedsFormField.ADL_NEED_NEW_NOTE]: adlNewNote,
        [ADLNeedsFormField.ADL_NEED_IS_ARCHIVED]: adlIsArchived,
        [ADLNeedsFormField.ADL_NEED_LAST_NOTE_TEXT]: adlLastNoteText,
        [ADLNeedsFormField.ADL_NEED_LAST_NOTE_AUTHOR]: adlLastNoteAuthor,
        [ADLNeedsFormField.ADL_NEED_LAST_NOTE_DATE]: adlLastNoteDate
      }
    })

    return {
      [ADLNeedsFormField.ADL_NEEDS]: adlneedsFormData
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []
    if (this.formDataFieldExists(ADLNeedsFormField.ADL_NEEDS)) {
      const adlNeeds = this.getFormDataField(ADLNeedsFormField.ADL_NEEDS)
      const adlItemsAndValue = adlNeeds.map((adlNeed) => {
        const observationReference = adlNeed[ADLNeedsFormField.ADL_NEED_OBSERVATION_REFERENCE]
        return {
          item: createAnswerItemsFromValues([
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_ID,
              adlNeed[ADLNeedsFormField.ADL_NEED_ID]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_OBSERVATION_REFERENCE,
              observationReference ? { reference: observationReference } : undefined
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_NAME,
              adlNeed[ADLNeedsFormField.ADL_NEED_NAME]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_NEW_NOTE,
              adlNeed[ADLNeedsFormField.ADL_NEED_NEW_NOTE]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_IS_ARCHIVED,
              adlNeed[ADLNeedsFormField.ADL_NEED_IS_ARCHIVED]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_TEXT,
              adlNeed[ADLNeedsFormField.ADL_NEED_LAST_NOTE_TEXT]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_AUTHOR,
              adlNeed[ADLNeedsFormField.ADL_NEED_LAST_NOTE_AUTHOR]
            ],
            [
              SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED_LAST_NOTE_DATE,
              adlNeed[ADLNeedsFormField.ADL_NEED_LAST_NOTE_DATE]
            ]
          ])
        }
      })
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.ADL_NEEDS_NEED,
        value: adlItemsAndValue
      })
    }
    return mutations
  }
}
